/*
 * Copyright (c) 2025 Amazon.com, Inc. or its affiliates. All rights reserved.
 *
 * PROPRIETARY/CONFIDENTIAL. USE IS SUBJECT TO LICENSE TERMS.
 */

import {useBundle} from '@amzn/react-arb-tools';
import {HOMEPAGE_MESSAGE_BUNDLE_NAME} from 'src/constants';
import {t} from 'src/helpers';
import {Button} from 'src/theming/components/Button';
import {Screen} from 'src/theming/components/Screen';
import * as styles from './styles/SubmissionErrorScreen.module.scss';
import {usePhotoFormController} from 'src/photoForm/PhotoFormController';
import {Spinner} from 'src/theming/components/Spinner';
import {isDevelopmentStage, STAGE} from 'src/utils/environment';

export const SubmissionErrorScreen = () => {
  const [bundle] = useBundle(HOMEPAGE_MESSAGE_BUNDLE_NAME);
  const {
    formState: {errors},
    reset,
    lastSubmissionResult,
  } = usePhotoFormController();

  return (
    <Screen className={styles.container}>
      {/* Placeholder div for spacing */}
      <div />
      <Spinner className={styles.spinner} />

      <p className={styles.errorDescription}>
        {errors.images?.message ?? t(bundle, 'submission_error_description')}
      </p>

      {/* Show additional development information for alpha/beta stages */}
      {isDevelopmentStage() && (
        <div className={styles.devInfo}>
          <p className={styles.devStageInfo}>
            <strong>Development Stage:</strong> {STAGE}
          </p>

          {lastSubmissionResult instanceof Error && (
            <div className={styles.errorDetails}>
              <p className={styles.errorMessage}>
                <strong>Error:</strong> {lastSubmissionResult.message}
              </p>

              {lastSubmissionResult.stack && (
                <details className={styles.stackTrace}>
                  <summary>Stack Trace</summary>
                  <pre className={styles.stackContent}>
                    {lastSubmissionResult.stack}
                  </pre>
                </details>
              )}
            </div>
          )}

          <p className={styles.devHelpText}>
            Check the browser console and any error displays for detailed
            debugging information.
          </p>
        </div>
      )}

      <Button
        className={styles.retryButton}
        label={t(bundle, 'try_again')}
        fullWidth
        onClick={() => {
          reset();
        }}
      />
    </Screen>
  );
};
